import { Link } from "@tanstack/react-router";
import { AlertTriangle, ShieldAlert, ShieldX } from "lucide-react";
import { Alert, buttonClasses } from "@fiskhub/ui";
import { useOverview } from "@/api/hooks";
import { CERTIFICATE_TIER, certificateAlerts } from "@/lib/status";
import { formatDay } from "@/lib/format";
import type { CertificateExpiry } from "@/api/types";

/**
 * The certificate expiry banner, shown on every screen.
 *
 * §8: an expired certificate halts that company's ENTIRE fleet. Notices
 * escalate at 60 / 30 / 14 / 7 / 1 days, and the visual weight escalates with
 * them — a 60-day notice is a quiet informational line, a 7-day one is a heavy
 * red alert. A missing or already-expired certificate is treated as the loudest
 * tier there is, because the fleet is already down.
 */

const ICONS = {
  1: AlertTriangle,
  2: AlertTriangle,
  3: ShieldAlert,
  4: ShieldX,
} as const;

function tone(severity: number): "info" | "warning" | "danger" {
  if (severity >= 4) return "danger";
  if (severity >= 2) return "warning";
  return "info";
}

function detail(row: CertificateExpiry): string {
  if (row.tier === "missing") {
    return "Upload the PKCS#12 issued for this company's OIB. The Tax Administration rejects any message whose payload OIB differs from the certificate OIB (error s005).";
  }
  if (row.tier === "expired") {
    return `Expired ${formatDay(row.not_after)}. Every sale from this company's ${row.device_count} device${row.device_count === 1 ? "" : "s"} is failing with a certificate error until it is replaced.`;
  }
  return `Valid until ${formatDay(row.not_after)}. Replacements are issued by your certificate provider, not by FiskHub — allow several working days.`;
}

export function CertificateBanner() {
  const overview = useOverview();
  const alerts = certificateAlerts(overview.data?.certificates ?? []);
  if (alerts.length === 0) return null;

  const shown = alerts.slice(0, 2);
  const remaining = alerts.length - shown.length;

  return (
    <div className="space-y-2">
      {shown.map((row) => {
        const tier = CERTIFICATE_TIER[row.tier];
        const Icon = ICONS[tier.severity as 1 | 2 | 3 | 4] ?? AlertTriangle;
        return (
          <Alert
            key={row.company_id}
            tone={tone(tier.severity)}
            emphasis={tier.severity >= 3}
            icon={<Icon className="size-5" aria-hidden="true" />}
            title={tier.headline(row)}
            actions={
              <Link
                to="/certificates"
                search={{ company: row.company_id }}
                className={buttonClasses({
                  size: "sm",
                  variant: tier.severity >= 3 ? "default" : "outline",
                })}
              >
                {row.tier === "missing" ? "Upload certificate" : "Manage certificates"}
              </Link>
            }
          >
            {detail(row)}
          </Alert>
        );
      })}

      {remaining > 0 ? (
        <p className="text-xs text-muted-foreground">
          <Link to="/certificates" className="underline underline-offset-2">
            {remaining} more {remaining === 1 ? "company needs" : "companies need"} certificate
            attention
          </Link>
        </p>
      ) : null}
    </div>
  );
}
